/**
 * Inverted upload flow (#11): PDF picker first, then the report name field.
 * On file pick we guess from the filename so the user can edit before submit;
 * AIP-1 overwrites with the canonical name during ingest.
 *
 * FYE is intentionally absent — it's now a per-company setting (#4), and the
 * fiscal year a given upload belongs to is derived from AIP-1's period_end.
 */
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormField,
  Input,
  Modal,
  Select,
  SpaceBetween,
} from "@cloudscape-design/components";
import type { Report } from "@/core/api/types";
import { createReportAndUpload, uploadToExistingReport } from "./api";

function fiscalYearChoices(): { label: string; value: string }[] {
  const year = new Date().getFullYear();
  return [year - 1, year, year + 1].map((y) => ({ label: `FY${y}`, value: `FY${y}` }));
}

/** #C6: a placeholder's inherited fy_end (a date) → the "FYyyyy" select value. */
function fyLabelFromDate(fyEnd?: string | null): string | undefined {
  if (!fyEnd) return undefined;
  const y = new Date(fyEnd).getFullYear();
  return Number.isFinite(y) ? `FY${y}` : undefined;
}

interface Props {
  visible: boolean;
  onDismiss: () => void;
  onUploaded: (reportId: string) => void;
  /** #C6: when set, upload INTO this 'awaiting_upload' placeholder (carve-out SOC 1)
   *  instead of creating a new report. Name + FY are pre-filled and editable. */
  existingReport?: Report;
}

function guessNameFromFilename(name: string): string {
  return name
    .replace(/\.pdf$/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s*soc\s*1\s*/i, "")
    .replace(/\s*type\s*(ii|2)\s*/i, "")
    .replace(/\s*\d{4}([-/]\d{2}){0,2}/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

export function UploadModal({ visible, onDismiss, onUploaded, existingReport }: Props) {
  const [file, setFile] = useState<File | null>(null);
  // #C6: a placeholder pre-fills its name; a fresh upload starts blank.
  const [reportName, setReportName] = useState(existingReport?.service_org_name ?? "");
  // Track whether the user has manually edited the name. If they have,
  // re-picking a file preserves their edit; otherwise the new filename
  // overwrites the guess (#68). A placeholder's pre-filled name counts as
  // "edited" so picking a file doesn't clobber the carve-out's name.
  const [nameEdited, setNameEdited] = useState(!!existingReport);
  const [fiscalYear, setFiscalYear] = useState<string>(
    fyLabelFromDate(existingReport?.fy_end) ?? `FY${new Date().getFullYear()}`
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fyOptions = fiscalYearChoices();

  const onPickFile = (picked: File | null) => {
    setFile(picked);
    if (picked && !nameEdited) {
      setReportName(guessNameFromFilename(picked.name));
    }
  };

  const reset = () => {
    setFile(null);
    // For a placeholder, restore its pre-filled name; for a fresh upload, clear.
    setReportName(existingReport?.service_org_name ?? "");
    setNameEdited(!!existingReport);
    setError(null);
  };

  const canSubmit = !!file && !busy;

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      if (existingReport) {
        await uploadToExistingReport(existingReport.id, file, {
          service_org_name: reportName.trim(),
          fiscal_year: fiscalYear,
        });
        onUploaded(existingReport.id);
      } else {
        const { report_id } = await createReportAndUpload(
          { service_org_name: reportName.trim(), fiscal_year: fiscalYear },
          file
        );
        onUploaded(report_id);
      }
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      visible={visible}
      onDismiss={() => {
        if (!busy) {
          onDismiss();
          reset();
        }
      }}
      header="Upload SOC 1 report"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss} disabled={busy}>
              Cancel
            </Button>
            <Button variant="primary" onClick={submit} disabled={!canSubmit} loading={busy}>
              Upload and process
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        {error && (
          <Alert type="error" header="Upload failed">
            {error}
          </Alert>
        )}

        <FormField
          label="SOC 1 PDF"
          description="Max 100MB. AI extracts the canonical report name during ingest."
        >
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
            disabled={busy}
          />
          {file && (
            <Box variant="small" color="text-body-secondary" padding={{ top: "xs" }}>
              {file.name} · {(file.size / 1024 / 1024).toFixed(1)} MB
            </Box>
          )}
        </FormField>

        <FormField
          label="Report name"
          description={
            existingReport
              ? "Pre-filled from the subservice organization — edit if needed. AIP-1 overwrites with the canonical name during ingest."
              : "Guessed from filename — edit to taste. AIP-1 overwrites with the canonical name from the report during ingest."
          }
        >
          <Input
            value={reportName}
            onChange={({ detail }) => {
              setReportName(detail.value);
              setNameEdited(true);
            }}
            placeholder="(leave blank to let AI fill in)"
            // #C6: a placeholder's name is pre-filled (not guessed from a filename),
            // so it's editable right away; a fresh upload still waits for a file.
            disabled={busy || (!existingReport && !file)}
          />
        </FormField>

        <FormField
          label="Cover fiscal year"
          description="Which of the user organization's fiscal years does this SOC 1 need to cover? Drives the C2 period-overlap check."
        >
          <Select
            selectedOption={fyOptions.find((o) => o.value === fiscalYear) ?? fyOptions[1] ?? fyOptions[0]!}
            onChange={({ detail }) => setFiscalYear(detail.selectedOption.value as string)}
            options={fyOptions}
            disabled={busy}
          />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
